// The one rule for a name a person types that others read: a display name, a server, a category, a collection, a
// sticker, an app or a bot (#382). Cases: shared/cases/clean-name.json.
//
// A direction control (U+202E, the isolates) changes how the text after it displays, so one name can reverse the text
// that follows it in a message, a mention or a notification. A zero-width character lets two names that look the same
// be different. cleanName takes both out, with the C0 and C1 controls. It does not run on message bodies: real Arabic
// and Hebrew text uses the marks and the isolates (messages are drawn in their own isolate instead).

/** How much of a name is read at all. No name cap comes near this (the longest is 50), and the cost of a huge input stays flat. */
const MAX_INPUT = 4096;

const ZWJ = 0x200d;
const ZWNJ = 0x200c;
const isJoiner = (cp: number) => cp === ZWJ || cp === ZWNJ;

/** The controls that are a space between two words in a name. */
const isSpaceControl = (cp: number) => (cp >= 0x09 && cp <= 0x0d) || cp === 0x85;

const DEFAULT_IGNORABLE = /^\p{Default_Ignorable_Code_Point}$/u;
const EMOJI = /^\p{Emoji}$/u;

/**
 * Code points that draw nothing and can only hide or mislead: the C0 and C1 controls, a lone surrogate, the interlinear
 * marks U+FFF9 to U+FFFB, and every default-ignorable code point (the soft hyphen, the combining grapheme joiner, the
 * Arabic letter mark, the Hangul fillers, the Khmer inherent vowels, the Mongolian and the other variation selectors,
 * the zero-width characters, the joiners, the direction controls, the word joiner and the invisible operators, the BOM,
 * the language and other tag characters, the shorthand and musical format controls). `cleanName` keeps five kinds
 * again, by context: a joiner or non-joiner, a variation selector 15 or 16 after an emoji, the tags of a flag, an
 * ideographic variation selector after a Han ideograph, and a Mongolian free variation selector between two Mongolian
 * letters. `stripHidden` keeps none of them.
 */
function hidden(cp: number): boolean {
  if (cp <= 0x1f || (cp >= 0x7f && cp <= 0x9f) || (cp >= 0xfff9 && cp <= 0xfffb) || (cp >= 0xd800 && cp <= 0xdfff)) return true;
  if (cp < 0xad) return false;
  return DEFAULT_IGNORABLE.test(String.fromCodePoint(cp));
}

/** An ideographic variation selector (IVS): the selector that picks one glyph of a Han ideograph. */
const isIvs = (cp: number) => cp >= 0xe0100 && cp <= 0xe01ef;
/** A Mongolian free variation selector (FVS1 to FVS3, and FVS4 from Unicode 14). */
const isFvs = (cp: number) => (cp >= 0x180b && cp <= 0x180d) || cp === 0x180f;

/** A joiner, a variation selector and a tag character: hidden, but `cleanName` may keep one by its context. */
const isCandidate = (cp: number) => isJoiner(cp) || cp === 0xfe0e || cp === 0xfe0f || (cp >= 0xe0020 && cp <= 0xe007f) || isIvs(cp) || isFvs(cp);

/**
 * The emoji sequences a joiner or a tag may be part of, as the Unicode "recommended for general interchange" lists
 * (RGI_Emoji_ZWJ_Sequence: a family, a couple with a heart, a person with a job, a rainbow flag; RGI_Emoji_Tag_Sequence:
 * the flags of England, Scotland and Wales). An engine without the `v` flag has no list, and then no joiner or tag
 * stays inside an emoji.
 */
const RGI = (() => {
  try {
    return new RegExp("\\p{RGI_Emoji_ZWJ_Sequence}|\\p{RGI_Emoji_Tag_Sequence}", "gv");
  } catch {
    return null;
  }
})();

const scriptTest = (script: string) => new RegExp(`^\\p{Script_Extensions=${script}}$`, "u");
/** The Indic scripts: they join only after a virama (a conjunct, or a visible virama). Myanmar and Khmer are not among them. */
const INDIC_JOINER_SCRIPTS = ["Devanagari", "Bengali", "Gurmukhi", "Gujarati", "Oriya", "Tamil", "Telugu", "Kannada", "Malayalam", "Sinhala"].map(scriptTest);
/**
 * The scripts whose words use a joiner or a non-joiner: Persian and Arabic write a word with a non-joiner, and the
 * Indic, Myanmar and Khmer scripts write a conjunct or a chillu with a joiner. Hebrew is not here: it joins nothing,
 * so a non-joiner between two Hebrew letters only tells two names apart.
 */
const JOINER_SCRIPTS = [...["Arabic", "Syriac", "Nko", "Myanmar", "Khmer", "Mongolian"].map(scriptTest), ...INDIC_JOINER_SCRIPTS];
const LETTER_OR_MARK = /^[\p{L}\p{M}]$/u;

/**
 * The viramas of the Indic scripts in JOINER_SCRIPTS (the sign that takes the vowel off a consonant, or in Sinhala the
 * al-lakuna): Devanagari, Bengali, Gurmukhi, Gujarati, Oriya, Tamil, Telugu, Kannada, Malayalam, Sinhala. JavaScript
 * has no Indic_Syllabic_Category, so this is the list. A test names one per script.
 */
export const INDIC_VIRAMAS: ReadonlySet<number> = new Set([0x094d, 0x09cd, 0x0a4d, 0x0acd, 0x0b4d, 0x0bcd, 0x0c4d, 0x0ccd, 0x0d4d, 0x0dca]);

/**
 * The Arabic-script letters of U+0600 to U+08FF that do not join the letter after them: Joining_Type Right_Joining
 * (alef, dal, dhal, reh, zain, waw, teh marbuta, jeh and the like) and Non_Joining (hamza), from ArabicShaping.txt of
 * Unicode 18. A non-joiner after one of them changes nothing in how the word draws, so it can only tell two names
 * apart (`د‌وست` against `دوست`). A letter not listed here, or an Arabic letter outside the range, keeps its non-joiner.
 */
export const ARABIC_NON_LEFT_JOINING: readonly (readonly [number, number])[] = [
  [0x621, 0x625], [0x627, 0x627], [0x629, 0x629], [0x62f, 0x632], [0x648, 0x648], [0x671, 0x677], [0x688, 0x699],
  [0x6c0, 0x6c0], [0x6c3, 0x6cb], [0x6cd, 0x6cd], [0x6cf, 0x6cf], [0x6d2, 0x6d3], [0x6d5, 0x6d5], [0x6e5, 0x6e6],
  [0x6ee, 0x6ef], [0x759, 0x75b], [0x76b, 0x76c], [0x771, 0x771], [0x773, 0x774], [0x778, 0x779], [0x870, 0x882],
  [0x887, 0x887], [0x88e, 0x88e], [0x8aa, 0x8ae], [0x8b1, 0x8b2], [0x8b9, 0x8b9], [0x8c9, 0x8c9],
];
const ARABIC_SCRIPT = /^\p{Script_Extensions=Arabic}$/u;
const ARABIC_LETTER = /^(?=\p{L})\p{Script_Extensions=Arabic}$/u;
const MARK_NONSPACING = /^\p{Mn}$/u;
const isNonLeftJoining = (cp: number) => ARABIC_NON_LEFT_JOINING.some(([a, b]) => cp >= a && cp <= b);

const HAN = /^\p{Script=Han}$/u;
const MONGOLIAN_LETTER = /^(?=\p{L})\p{Script_Extensions=Mongolian}$/u;
const EMOJI_PRESENTATION = /^\p{Emoji_Presentation}$/u;
const isHan = (cp: number | undefined) => cp !== undefined && HAN.test(String.fromCodePoint(cp));
const isMongolianLetter = (cp: number | undefined) => cp !== undefined && MONGOLIAN_LETTER.test(String.fromCodePoint(cp));

/** True when both code points are letters or marks of one script that uses joiners. A Mongolian variation selector is no mark here: it is kept or dropped for itself. */
function sameJoinerScript(a: number | undefined, b: number | undefined): boolean {
  if (a === undefined || b === undefined || isFvs(a) || isFvs(b)) return false;
  const x = String.fromCodePoint(a);
  const y = String.fromCodePoint(b);
  if (!LETTER_OR_MARK.test(x) || !LETTER_OR_MARK.test(y)) return false;
  return JOINER_SCRIPTS.some((script) => script.test(x) && script.test(y));
}

/**
 * Whether a joiner (`cp`) between `before` and `after`, two letters or marks of one joiner script, is real. An Indic
 * joiner is real after a virama only: after a plain consonant it changes nothing (`क‌म`). An Arabic non-joiner is real
 * after a letter that joins the next one: `base` is the last letter of `out` before `before`'s marks.
 */
function joinerIsReal(cp: number, before: number, after: number, base: number): boolean {
  const x = String.fromCodePoint(before);
  const y = String.fromCodePoint(after);
  if (INDIC_JOINER_SCRIPTS.some((script) => script.test(x) && script.test(y))) return INDIC_VIRAMAS.has(before);
  if (cp === ZWNJ && ARABIC_SCRIPT.test(x) && ARABIC_SCRIPT.test(y) && ARABIC_LETTER.test(String.fromCodePoint(base)) && isNonLeftJoining(base)) return false;
  return true;
}

/** The last code point of `out` that is not a nonspacing mark (a vowel sign or a diacritic sits on its letter). */
function lastBase(out: number[]): number {
  let i = out.length - 1;
  while (i > 0 && MARK_NONSPACING.test(String.fromCodePoint(out[i]!))) i--;
  return out[i]!;
}

let segmenter: Intl.Segmenter | null | undefined;

/** The longest start of `s` that holds whole grapheme clusters and is at most `max` UTF-16 units. */
function capAtGrapheme(s: string, max: number): string {
  if (s.length <= max) return s;
  if (segmenter === undefined) segmenter = typeof Intl !== "undefined" && "Segmenter" in Intl ? new Intl.Segmenter(undefined, { granularity: "grapheme" }) : null;
  let end = 0;
  if (segmenter) {
    for (const { segment } of segmenter.segment(s)) {
      if (end + segment.length > max) break;
      end += segment.length;
    }
  } else {
    // No Intl.Segmenter (an old browser): whole code points, and never a cut before a joiner, a variation selector,
    // a skin tone, a combining mark, a keycap or a tag character.
    const sticks = (ch: string) => {
      const cp = ch.codePointAt(0)!;
      return isJoiner(cp) || cp === 0xfe0e || cp === 0xfe0f || cp === 0x20e3 || (cp >= 0x1f3fb && cp <= 0x1f3ff) || cp >= 0xe0020 && cp <= 0xe007f || /^\p{M}$/u.test(ch);
    };
    const points = [...s];
    let i = 0;
    while (i < points.length) {
      let unit = points[i]!.length;
      let j = i + 1;
      while (j < points.length && sticks(points[j]!)) {
        const joiner = points[j] === "\u200d";
        unit += points[j]!.length;
        j++;
        if (joiner && j < points.length) unit += points[j++]!.length;
      }
      if (end + unit > max) break;
      end += unit;
      i = j;
    }
  }
  return s.slice(0, end);
}

const SKIN_TONE_FIRST = 0x1f3fb;
const SKIN_TONE_LAST = 0x1f3ff;
const isSkinTone = (cp: number | undefined) => cp !== undefined && cp >= SKIN_TONE_FIRST && cp <= SKIN_TONE_LAST;
/** An emoji that can start or end a ZWJ element: not a digit, # or * (they are Emoji for keycaps), not a skin tone. */
const isEmojiBase = (cp: number | undefined) => cp !== undefined && !isSkinTone(cp) && !(cp >= 0x30 && cp <= 0x39) && cp !== 0x23 && cp !== 0x2a && EMOJI.test(String.fromCodePoint(cp));

/** The most elements one RGI ZWJ sequence has is 4 (a kiss with two skin tones); 8 leaves room, and keeps the work for a long chain flat. */
const MAX_ZWJ_ELEMENTS = 8;
const RGI_ZWJ_WHOLE = (() => {
  try {
    return new RegExp("^(?:\\p{RGI_Emoji_ZWJ_Sequence})$", "v");
  } catch {
    return null;
  }
})();

interface ZwjElement {
  /** `kept[start..end)`: an emoji, then a skin tone or a variation selector 16. */
  start: number;
  end: number;
  /** True when the form needs a variation selector 16 to be fully qualified, and has none. */
  needsVs16: boolean;
}

/** The emoji element at `kept[i]`, or null (not an emoji, or it asks for the text form with a variation selector 15). */
function zwjElementAt(kept: number[], i: number): ZwjElement | null {
  const cp = kept[i];
  if (!isEmojiBase(cp)) return null;
  let end = i + 1;
  let qualified = EMOJI_PRESENTATION.test(String.fromCodePoint(cp!));
  if (kept[end] === 0xfe0e) return null;
  if (isSkinTone(kept[end])) {
    end++;
    qualified = true;
  } else if (kept[end] === 0xfe0f) {
    end++;
    qualified = true;
  }
  return { start: i, end, needsVs16: !qualified };
}

/**
 * A ZWJ sequence typed without its variation selectors (🏳+ZWJ+🌈, ❤+ZWJ+🔥, 👁+ZWJ+🗨, 👨+ZWJ+⚕, as pasted text has
 * it) gets them back when the form with them is an RGI ZWJ sequence: the list holds only the fully qualified form, so
 * without this the joiner would go and the pieces fall apart. Anything that is not an RGI sequence once qualified is
 * left as it is.
 */
function qualifyZwjSequences(kept: number[]): number[] {
  if (!RGI_ZWJ_WHOLE || !kept.includes(ZWJ)) return kept;
  // A selector added grows the text. It never grows past what the name reads (MAX_INPUT), so reading a name again
  // gives the same name; a sequence that would not fit stays as typed (and loses its joiners).
  let room = MAX_INPUT - String.fromCodePoint(...kept).length;
  const out: number[] = [];
  let i = 0;
  while (i < kept.length) {
    const first = zwjElementAt(kept, i);
    if (!first) {
      out.push(kept[i]!);
      i++;
      continue;
    }
    // The chain of elements joined by ZWJ, up to the most an RGI sequence has.
    const chain = [first];
    for (let at = first.end; chain.length < MAX_ZWJ_ELEMENTS && kept[at] === ZWJ; ) {
      const next = zwjElementAt(kept, at + 1);
      if (!next) break;
      chain.push(next);
      at = next.end;
    }
    let done = false;
    // The longest start of the chain that is an RGI sequence once every element is fully qualified.
    for (let n = chain.length; n >= 2 && !done; n--) {
      const cps: number[] = [];
      let added = 0;
      chain.slice(0, n).forEach((el, k) => {
        if (k > 0) cps.push(ZWJ);
        for (let u = el.start; u < el.end; u++) cps.push(kept[u]!);
        if (el.needsVs16) {
          cps.push(0xfe0f);
          added++;
        }
      });
      if (added <= room && RGI_ZWJ_WHOLE.test(String.fromCodePoint(...cps))) {
        room -= added;
        out.push(...cps);
        i = chain[n - 1]!.end;
        done = true;
      }
    }
    if (!done) {
      for (let u = first.start; u < first.end; u++) out.push(kept[u]!);
      i = first.end;
    }
  }
  return out;
}

const VIRAMA_MALAYALAM = 0x0d4d;
const KEYCAP = 0x20e3;

/** Drops what is hidden and keeps a joiner, a variation selector or a tag only where the rules below allow it. */
function scrub(text: string): string {
  // Pass 1: drop what is hidden. The candidates wait for pass 2, which sees both neighbors.
  let kept: number[] = [];
  for (const ch of text) {
    const cp = ch.codePointAt(0)!;
    if (isSpaceControl(cp)) kept.push(0x20);
    else if (!hidden(cp) || isCandidate(cp)) kept.push(cp);
  }
  kept = qualifyZwjSequences(kept);
  const joined = String.fromCodePoint(...kept);
  // The parts of the text that are an RGI emoji sequence, as ranges of UTF-16 units. Everything in one stays.
  const ranges: [number, number][] = RGI ? [...joined.matchAll(RGI)].map((m) => [m.index!, m.index! + m[0].length]) : [];
  // Pass 2.
  const out: number[] = [];
  let unit = 0;
  let r = 0;
  for (let i = 0; i < kept.length; i++) {
    const cp = kept[i]!;
    const start = unit;
    unit += cp > 0xffff ? 2 : 1;
    while (r < ranges.length && ranges[r]![1] <= start) r++;
    if (r < ranges.length && start >= ranges[r]![0]) {
      out.push(cp);
      continue;
    }
    if (!isCandidate(cp)) {
      out.push(cp);
      continue;
    }
    const before = out[out.length - 1];
    const after = kept[i + 1];
    if (isJoiner(cp)) {
      // Between two letters or marks of one script that uses it, where it changes how the word draws (an Indic joiner after a virama, an Arabic non-joiner after a letter that joins the next). Or the old Malayalam chillu: a joiner after a virama at the end of a word.
      if (sameJoinerScript(before, after)) {
        if (joinerIsReal(cp, before!, after!, lastBase(out))) out.push(cp);
      } else if (cp === ZWJ && before === VIRAMA_MALAYALAM && (after === undefined || !LETTER_OR_MARK.test(String.fromCodePoint(after)))) out.push(cp);
    } else if (cp === 0xfe0e || cp === 0xfe0f) {
      // Only right after an emoji that does not already draw as one. After a digit, # or * only in a keycap.
      if (before !== undefined && EMOJI.test(String.fromCodePoint(before))) {
        const keycapBase = (before >= 0x30 && before <= 0x39) || before === 0x23 || before === 0x2a;
        if (keycapBase ? cp === 0xfe0f && after === KEYCAP : !EMOJI_PRESENTATION.test(String.fromCodePoint(before))) out.push(cp);
      }
    } else if (isIvs(cp)) {
      // Right after a Han ideograph: it picks that ideograph's glyph (葛 + U+E0100).
      if (isHan(before)) out.push(cp);
    } else if (isFvs(cp)) {
      // Between two Mongolian letters: it picks the glyph of the letter before it.
      if (isMongolianLetter(before) && isMongolianLetter(after)) out.push(cp);
    }
    // A tag character outside an RGI flag is dropped.
  }
  return String.fromCodePoint(...out);
}

/**
 * The end of a name the cap cut: no joiner, non-joiner or Mongolian variation selector left dangling (it was between
 * two letters, and the second is gone). A Malayalam chillu joiner after a virama is a whole word end.
 */
function dropTrailingJoiners(s: string): string {
  let end = s.length;
  while (end > 0) {
    const last = s.charCodeAt(end - 1);
    if (!isJoiner(last) && !isFvs(last)) break;
    if (last === ZWJ && end >= 2 && s.charCodeAt(end - 2) === VIRAMA_MALAYALAM) break;
    end--;
  }
  return s.slice(0, end);
}

/**
 * A name as the room keeps it: no hidden character, NFKC, one space between words, trimmed, and at most `max` UTF-16
 * units (cut between grapheme clusters, never inside an emoji). "" when nothing is left, which the caller refuses.
 * Hidden means every default-ignorable code point (so a variation selector, a Hangul filler, a combining grapheme
 * joiner, a direction control and a zero-width character), the C0 and C1 controls and a lone surrogate. Five
 * things stay, by their context:
 *  - A joiner or non-joiner: inside an RGI emoji sequence (a family, a skin tone, a rainbow flag; the list is
 *    RGI_Emoji_ZWJ_Sequence); between two letters or marks of one script that writes words with it, where it changes
 *    how the word draws (Persian: a non-joiner after a letter that joins the next one; Hindi and the other Indic
 *    scripts: after a virama; Malayalam: also after a virama at the end of a word, the old chillu). It goes at either
 *    end, next to a space, between Latin letters, between two scripts, and between two Hebrew letters.
 *    A ZWJ sequence typed without its variation selectors (🏳+ZWJ+🌈) gets them back first, when the form with them
 *    is an RGI sequence (🏳️‍🌈).
 *  - Variation selector 15 or 16: right after an emoji that does not already draw as one (❤️, ☺︎; not 👍️), or in a
 *    keycap (after a digit, # or * only there).
 *  - A tag character: in an RGI subdivision flag (England, Scotland, Wales).
 *  - An ideographic variation selector (U+E0100 to U+E01EF): right after a Han ideograph (葛󠄀).
 *  - A Mongolian free variation selector (U+180B to U+180D, U+180F): between two Mongolian letters.
 * The text is stripped, normalized, and stripped again until it stops changing, because NFKC can join a mark to a
 * letter that a hidden character kept apart, and can produce a hidden character.
 */
export function cleanName(raw: unknown, max = Infinity): string {
  let text = scrub(String(raw ?? "").slice(0, MAX_INPUT));
  for (let i = 0; i < 16; i++) {
    // NFKC can lengthen a text (U+FDFA becomes 18 code points), so the bound is applied again.
    const next = scrub(text.normalize("NFKC").slice(0, MAX_INPUT)).replace(/\s+/g, " ").trim();
    if (next === text) break;
    text = next;
  }
  const capped = capAtGrapheme(text, max);
  return capped.length === text.length ? capped : dropTrailingJoiners(capped).trim();
}

/**
 * `text` without any character `cleanName` treats as hidden (controls, direction controls, zero-width characters, every
 * joiner and non-joiner, every variation selector, every tag character: all of them, with none kept by context), and
 * nothing else changed. For a check on text that is never stored or shown, such as a working title taken from a
 * build request.
 */
export function stripHidden(text: string): string {
  let out = "";
  for (const ch of text) {
    const cp = ch.codePointAt(0)!;
    if (isSpaceControl(cp)) out += " ";
    else if (!hidden(cp)) out += ch;
  }
  return out;
}
